import { useEffect, useState } from "react";

// ASSETS
import LeftArrow from "assets/icons/ArrowLeftHovered.svg";
import CoverPlaceholder from "assets/img/CoverPlaceholder.png";
import { ReactComponent as NoDataDark } from "assets/svg/NoDataDark.svg";
import RedLoop from "assets/svg/SearchIconRed.svg";

// STYLES
import {
  BackToGuidesButton,
  BackToGuidesImage,
  BackToGuidesWrapper,
  CardContainer,
  ImgStyled,
  InputContainer,
  NoDataContainer,
  OfficialAnnouncementsContainer,
  SvgContainer,
  TitleWrapper,
  Wrapper,
} from "./CampaignArchiveStyled";

// LIBRARIES
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// MISC
import { ButtonType } from "components/Atoms/ButtonAtom/ButtonAtomModel";
import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import { OptionType } from "components/Atoms/InputAtom/InputAtomModel";
import { appRoutes } from "config/AppRoutesConfig";
import { COLORS } from "constants/variables";
import { secondUploadBaseUrl } from "services/helperBase";
import timeAgo from "utils/timeAgo";

// COMPONENTS
import ButtonAtom from "components/Atoms/ButtonAtom/ButtonAtom";
import CustomText from "components/Atoms/CustomText/CustomText";
import InputAtom from "components/Atoms/InputAtom/InputAtom";
import NewsCard from "components/NewsCard/NewsCard";
import mapService from "services/mapService";

// REDUX
import { useSelector } from "react-redux";
import { accessibilityBiggerCursorSelector } from "slices/UISlice/uiSliceSelectors";

const CampaignArchive = () => {
  // LIBRARY CONSTANTS
  const { t } = useTranslation();
  const navigate = useNavigate();
  const biggerCursor = useSelector(accessibilityBiggerCursorSelector);

  // STATE CONSTANTS
  const [inputText, setInputText] = useState<string>("");
  const [options, setOptions] = useState<OptionType[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState<any[]>([]); // New state for filtered campaigns

  // LIFE CYCLE
  useEffect(() => {
    // Fetch the campaigns when the component loads
    mapService
      .getCampaigns()
      .then((response) => {
        const tmpCampaigns = response.data.map((campaign: any) => {
          const isVideo =
            campaign.attributes.cover.data.attributes.mime.includes("video");
          return {
            id: campaign?.id,
            date: campaign?.attributes?.publishedAt,
            title: campaign?.attributes?.title,
            headerTitle: "Campanie " + campaign?.id,
            text: campaign?.attributes.content[0].children[0].text,
            isVideo: isVideo,
            onCardClick: () => navigate(`/campanii/${campaign.id}`),
            img: `${secondUploadBaseUrl}${campaign.attributes.cover.data.attributes.url}`,
          };
        });
        setCampaigns(tmpCampaigns);
        setFilteredCampaigns(tmpCampaigns); // Initialize filtered campaigns with all campaigns
      })
      .catch((err) => {
        console.log("getCampaigns error", err);
      });
  }, [navigate]);

  // EFFECT FOR SEARCHING WHILE TYPING
  useEffect(() => {
    // If the inputText is empty, show all campaigns
    if (inputText.trim() === "") {
      setFilteredCampaigns(campaigns);
    } else {
      // Filter campaigns based on the input text
      const filtered = campaigns.filter((campaign) =>
        campaign.title.toLowerCase().includes(inputText.toLowerCase())
      );
      setFilteredCampaigns(filtered);
    }
  }, [inputText, campaigns]); // This effect runs whenever inputText or campaigns changes

  return (
    <OfficialAnnouncementsContainer>
      <BackToGuidesWrapper
        onClick={() => navigate(appRoutes.dialog)}
        biggerCursor={biggerCursor}
      >
        <BackToGuidesImage src={LeftArrow} alt="backIcon" />
        <BackToGuidesButton biggerCursor={biggerCursor}>
          <CustomText
            text={t("view.campaignArchive.backButton")}
            type={TextType.H5boldspaced}
            color={COLORS.RaisinBlack}
          />
        </BackToGuidesButton>
      </BackToGuidesWrapper>
      <TitleWrapper>
        <CustomText text={t("view.campaignArchive.title")} type={TextType.H2} />
      </TitleWrapper>
      <InputContainer>
        {/* No need for a form anymore, since we are searching on input change */}
        <InputAtom
          startAdornment={<ImgStyled src={RedLoop} alt="RedLoopIcon" />}
          endAdornment={
            <ButtonAtom
              text={t("view.newsAndMedia.officialAnnouncements.search")}
              type={ButtonType.ArrowDownDefault}
            />
          }
          placeholder={t(
            "view.newsAndMedia.officialAnnouncements.searchPlaceholder"
          )}
          isSearchBar={true}
          options={options}
          onChange={(e) => setInputText(e.target.value)} // Set inputText on typing
          isModal={true}
        />
      </InputContainer>
      {filteredCampaigns.length > 0 ? (
        <Wrapper>
          <CardContainer>
            {filteredCampaigns?.map((card) => (
              <NewsCard
                key={card?.id}
                id={card?.id}
                img={card.img ? card.img : CoverPlaceholder}
                title={card?.title}
                timeToRead={""}
                timeline={timeAgo(card.date)}
                route={appRoutes.campaignsDetails}
              />
            ))}
          </CardContainer>
        </Wrapper>
      ) : (
        <NoDataContainer>
          <SvgContainer>
            <NoDataDark />
          </SvgContainer>
          <CustomText
            text={t("app.noData")}
            type={TextType.P1}
            color={COLORS.MetalicSilver}
          />
        </NoDataContainer>
      )}
    </OfficialAnnouncementsContainer>
  );
};

export default CampaignArchive;
